import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  ChatAssistantMessage,
  ChatStreamingIndicator,
} from "@arcadeai/design-system/components/ui/molecules/chat-message-list";
import {
  Message,
  MessageContent,
} from "@arcadeai/design-system/components/ui/molecules/message";
import type { DynamicToolUIPart, ToolUIPart, UIMessage } from "ai";
import { getToolName, isTextUIPart, isToolUIPart } from "ai";
import { memo } from "react";
import type { AuthInfo } from "../types";
import { ToolInvocation } from "./tool-invocation";

function stringifyPart(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

type ChatMessageProps = {
  message: UIMessage;
  isLast: boolean;
  isStreaming: boolean;
  authInfoMap?: Map<string, AuthInfo>;
  onRetryAuth?: () => void;
};

function toToolCallDisplay(part: ToolUIPart | DynamicToolUIPart): {
  state: "calling" | "complete" | "error";
  result: unknown;
} {
  switch (part.state) {
    case "output-available":
      return { state: "complete", result: part.output };
    case "output-error":
      return {
        state: "error",
        result: part.approval
          ? { error: part.errorText, approval: part.approval }
          : { error: part.errorText },
      };
    case "output-denied":
      return {
        state: "error",
        result: { denied: true, approval: part.approval },
      };
    default:
      return { state: "calling", result: undefined };
  }
}

type RenderPartArgs = {
  message: UIMessage;
  part: UIMessage["parts"][number];
  isAnimating: boolean;
  index: number;
  authInfoMap?: Map<string, AuthInfo>;
  onRetryAuth?: () => void;
};

function renderPart({
  message,
  part,
  isAnimating,
  index,
  authInfoMap,
  onRetryAuth,
}: RenderPartArgs) {
  if (isTextUIPart(part)) {
    if (!part.text) {
      return null;
    }
    const isUser = message.role === "user";
    return (
      <MessageContent
        className={
          isUser
            ? "rounded-xl shadow-xs"
            : "prose-sm border-0 bg-transparent p-0 dark:border-transparent [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg"
        }
        isAnimating={isAnimating && part.state === "streaming"}
        key={`${message.id}-text-${index.toString()}`}
        markdown={!isUser}
      >
        {part.text}
      </MessageContent>
    );
  }

  if (isToolUIPart(part)) {
    const { state, result } = toToolCallDisplay(part);
    return (
      <ToolInvocation
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        args={(part.input ?? {}) as Record<string, unknown>}
        authInfoMap={authInfoMap}
        key={part.toolCallId}
        name={getToolName(part)}
        onRetryAuth={onRetryAuth}
        result={result}
        state={state}
      />
    );
  }

  if (part.type === "step-start") {
    return null;
  }

  const value = "data" in part ? part.data : part;
  return (
    <div
      className="space-y-1 rounded-lg border border-border/60 bg-muted/30 p-2.5"
      key={`${message.id}-${part.type}-${index.toString()}`}
    >
      <p className="font-mono text-muted-foreground text-xs">{part.type}</p>
      <pre className="whitespace-pre-wrap break-all font-mono text-xs">
        {stringifyPart(value)}
      </pre>
    </div>
  );
}

function hasVisibleContent(message: UIMessage): boolean {
  return message.parts.some(
    (part) =>
      (part.type === "text" && part.text) ||
      (part.type !== "text" && part.type !== "step-start")
  );
}

export const ChatMessage = memo(function ChatMessage({
  message,
  isLast,
  isStreaming,
  authInfoMap,
  onRetryAuth,
}: ChatMessageProps) {
  if (message.role === "user") {
    const content = (
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {message.parts.map((part, index) =>
          renderPart({
            message,
            part,
            isAnimating: false,
            index,
            authInfoMap,
            onRetryAuth,
          })
        )}
      </div>
    );
    return <Message className="ml-auto max-w-2xl">{content}</Message>;
  }

  // Show inline indicator while the assistant message has no visible content
  // yet. The standalone indicator in ChatMessages only covers the "submitted"
  // phase (before the assistant message object exists). Handling it here avoids
  // the gap-8 layout shift that occurs when the empty assistant list item pushes
  // the standalone indicator downward.
  if (isLast && isStreaming && !hasVisibleContent(message)) {
    return (
      <ChatAssistantMessage>
        <ChatStreamingIndicator />
      </ChatAssistantMessage>
    );
  }

  const content = (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      {message.role === "system" && <Badge variant="outline">System</Badge>}
      {message.parts.map((part, index) =>
        renderPart({
          message,
          part,
          isAnimating: isLast && isStreaming,
          index,
          authInfoMap,
          onRetryAuth,
        })
      )}
    </div>
  );

  return <ChatAssistantMessage>{content}</ChatAssistantMessage>;
});
